import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Link,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  SelectChangeEvent,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { keyframes } from '@mui/system';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import CareXLogo from '../components/CareXLogo';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useNavigate } from 'react-router-dom';

// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Mock data for tenants - in a real app, this would come from an API
const mockTenants = [
  { id: 'acme-healthcare', name: 'Acme Healthcare', primaryColor: '#1976d2' },
  { id: 'sunrise-senior', name: 'Sunrise Senior Living', primaryColor: '#7b1fa2' },
  { id: 'maple-grove', name: 'Maple Grove Care', primaryColor: '#388e3c' },
  { id: 'oak-park', name: 'Oak Park Nursing Home', primaryColor: '#f57c00' },
];

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [selectedTenant, setSelectedTenant] = useState('');
  const [error, setError] = useState('');
  const [loginTab, setLoginTab] = useState(0); // 0: Facility, 1: Super Admin
  const { login, isAuthenticated } = useAuth();
  const { setTenant, setCurrentTenantId } = useTenant();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        if (user.role === 'SUPER_ADMIN') {
          navigate('/super-admin/dashboard');
        } else {
          navigate('/dashboard');
        }
      } else {
        navigate('/dashboard');
      }
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // For facility login, tenant selection is required
    if (loginTab === 0 && !selectedTenant) {
      setError('Please select a facility');
      return;
    }
    
    try {
      const success = await login(username, password);
      if (success) {
        // Set the selected tenant after successful login (for facility login)
        if (loginTab === 0) {
          const tenant = mockTenants.find(t => t.id === selectedTenant);
          if (tenant) {
            setTenant({
              id: tenant.id,
              name: tenant.name,
              primaryColor: tenant.primaryColor,
            });
            setCurrentTenantId(tenant.id);
            localStorage.setItem('currentTenantId', tenant.id);
          }
        }
        
        // Redirect based on user role after login
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const user = JSON.parse(storedUser);
          if (user.role === 'SUPER_ADMIN') {
            navigate('/super-admin/dashboard');
          } else {
            navigate('/dashboard');
          }
        } else {
          navigate('/dashboard');
        }
      } else {
        setError('Invalid username or password');
      }
    } catch (err) {
      setError('An error occurred during login');
    }
  };

  const handleTenantChange = (event: SelectChangeEvent<string>) => {
    setSelectedTenant(event.target.value as string);
    setError(''); // Clear error when tenant is selected
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setLoginTab(newValue);
    setError(''); // Clear error when switching tabs
    setSelectedTenant(''); // Clear tenant selection when switching tabs
    setUsername(''); // Clear username when switching tabs
    setPassword(''); // Clear password when switching tabs
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #2CA6A4 0%, #174A5A 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)',
          pointerEvents: 'none',
        }
      }}
    >
      <Container component="main" maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4} alignItems="center">
          {/* Left Side - Branding & Features */}
          <Grid item xs={12} md={6} sx={{ animation: `${slideInLeft} 0.8s ease-out` }}>
            <Box sx={{ color: 'white', textAlign: { xs: 'center', md: 'left' } }}>
              <Box sx={{ mb: 4, display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <Box
                  sx={{
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '20px',
                    p: 3,
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  <CareXLogo style={{ marginBottom: 0 }} />
                </Box>
              </Box>
              <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
                CareX Nursing Home Software
              </Typography>
              <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, fontWeight: 300 }}>
                Comprehensive care management solution for modern healthcare facilities
              </Typography>

              {/* Features */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <SecurityIcon sx={{ fontSize: 32, flexShrink: 0, mt: 1 }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Secure Access
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>
                      HIPAA-compliant authentication
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <HealthAndSafetyIcon sx={{ fontSize: 32, flexShrink: 0, mt: 1 }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Patient Care
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>
                      Manage admissions and clinical notes
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <LockOutlinedIcon sx={{ fontSize: 32, flexShrink: 0, mt: 1 }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Multi-Facility
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>
                      Manage multiple facilities seamlessly
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Right Side - Login Form */}
          <Grid item xs={12} md={6} sx={{ animation: `${slideInRight} 0.8s ease-out` }}>
            <Card
              sx={{
                borderRadius: '16px',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                background: 'rgba(255, 255, 255, 0.95)',
              }}
            >
              <CardContent sx={{ pt: 5, pb: 4, px: 4 }}>
                <Tabs
                  value={loginTab}
                  onChange={handleTabChange}
                  centered
                  sx={{
                    mb: 3,
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#2CA6A4',
                    },
                    '& .MuiTab-root': {
                      color: '#999',
                      fontWeight: 500,
                      '&.Mui-selected': {
                        color: '#2CA6A4',
                      },
                    },
                  }}
                >
                  <Tab label="Facility Login" />
                  {/* <Tab label="Super Admin" /> */}
                </Tabs>

                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: '#666', mb: 3, fontWeight: 500 }}
                >
                  {loginTab === 0 ? 'Welcome back to your facility' : 'Super administrator access'}
                </Typography>

                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 2,
                      animation: `${fadeIn} 0.3s ease-out`,
                      borderRadius: '8px',
                    }}
                  >
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2 }}>
                  {loginTab === 0 && (
                    <FormControl fullWidth margin="normal" required>
                      <InputLabel id="tenant-select-label">Select Facility</InputLabel>
                      <Select
                        labelId="tenant-select-label"
                        id="tenant-select"
                        value={selectedTenant}
                        label="Select Facility"
                        onChange={handleTenantChange}
                        sx={{
                          borderRadius: '8px',
                          '& .MuiOutlinedInput-root': {
                            transition: 'all 0.3s',
                            '&:hover': {
                              borderColor: '#2CA6A4',
                            },
                            '&.Mui-focused': {
                              borderColor: '#2CA6A4',
                              boxShadow: '0 0 0 3px rgba(44, 166, 164, 0.1)',
                            },
                          },
                        }}
                      >
                        {mockTenants.map((tenant) => (
                          <MenuItem key={tenant.id} value={tenant.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar
                                sx={{
                                  width: 28,
                                  height: 28,
                                  fontSize: '0.75rem',
                                  bgcolor: tenant.primaryColor || '#1976d2',
                                  mr: 1.5,
                                }}
                              >
                                {tenant.name.charAt(0)}
                              </Avatar>
                              <Typography>{tenant.name}</Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="username"
                    label="Username"
                    name="username"
                    autoComplete="username"
                    autoFocus
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    helperText={loginTab === 1 ? "Use 'superadmin' for demo access" : ''}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        transition: 'all 0.3s',
                        '&:hover': {
                          borderColor: '#2CA6A4',
                        },
                        '&.Mui-focused': {
                          borderColor: '#2CA6A4',
                          boxShadow: '0 0 0 3px rgba(44, 166, 164, 0.1)',
                        },
                      },
                    }}
                  />

                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    helperText={loginTab === 1 ? 'Use any password for demo access' : ''}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        transition: 'all 0.3s',
                        '&:hover': {
                          borderColor: '#2CA6A4',
                        },
                        '&.Mui-focused': {
                          borderColor: '#2CA6A4',
                          boxShadow: '0 0 0 3px rgba(44, 166, 164, 0.1)',
                        },
                      },
                    }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{
                      mt: 4,
                      mb: 2,
                      py: 1.5,
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #2CA6A4 0%, #1B5E6A 100%)',
                      textTransform: 'none',
                      transition: 'all 0.3s',
                      boxShadow: '0 4px 15px rgba(44, 166, 164, 0.4)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(44, 166, 164, 0.6)',
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                    }}
                  >
                    {loginTab === 0 ? 'Sign In to Facility' : 'Sign In as Super Admin'}
                  </Button>

                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Link
                        href="#"
                        variant="body2"
                        sx={{
                          color: '#2CA6A4',
                          textDecoration: 'none',
                          fontWeight: 500,
                          transition: 'color 0.3s',
                          '&:hover': {
                            color: '#1B5E6A',
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        Forgot password?
                      </Link>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                      {/* <Link
                        href="#"
                        variant="body2"
                        sx={{
                          color: '#2CA6A4',
                          textDecoration: 'none',
                          fontWeight: 500,
                          transition: 'color 0.3s',
                          '&:hover': {
                            color: '#1B5E6A',
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        Contact Admin
                      </Link> */}
                    </Grid>
                  </Grid>
                </Box>

                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    mt: 4,
                    color: '#999',
                    lineHeight: 1.6,
                  }}
                >
                  © {new Date().getFullYear()} CareX Nursing Home Software. All rights reserved.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default LoginPage;